import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Heart, Trophy, Buildings, Sparkle, ArrowRight } from "@phosphor-icons/react";
import DigitalSetuLogo from "@/components/DigitalSetuLogo";

export default function PublicAbout() {
  return (
    <div className="max-w-6xl mx-auto px-4 py-14" data-testid="public-about">
      {/* Hero */}
      <div className="max-w-3xl mb-14">
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#1e3a8a] mb-1.5">About Us</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
          A digital bridge <span className="text-[#ea580c]">(setu)</span> between citizens and government services.
        </h1>
        <p className="text-lg text-slate-600 mt-5 leading-relaxed">
          DigitalSetu started as Priyanka Enterprise in 2015 — a small CSC helping neighbours get PAN cards and Aadhaar updates.
          A decade later, we serve thousands of families every month across multiple branches, powered by our own end-to-end platform.
        </p>
      </div>

      {/* Mission */}
      <div className="grid md:grid-cols-2 gap-10 items-center mb-20">
        <div className="rounded-3xl bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] p-10 text-white shadow-xl relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-500/20 rounded-full blur-3xl" />
          <div className="mb-4"><DigitalSetuLogo size={54} variant="mono" /></div>
          <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold mb-2">Our Mission</div>
          <p className="text-xl leading-relaxed font-medium" style={{ fontFamily: "var(--font-heading)" }}>
            To make every government service accessible, transparent, and dignified for every Indian — regardless of language, location or literacy.
          </p>
        </div>
        <div className="space-y-6">
          {[
            { icon: Heart,       title: "Human-first service",    desc: "We treat customers as neighbours. Same-day updates, no jargon, no runaround." },
            { icon: ShieldCheck, title: "Bank-grade security",    desc: "AES-256 encrypted storage. Your Aadhaar &amp; PAN details are safer with us than most banks." },
            { icon: Sparkle,     title: "Built by locals, for locals", desc: "Our platform is designed for Indian branches — Marathi + Hindi + English UI, WhatsApp-first workflows, UPI payments." },
          ].map((v) => {
            const Ic = v.icon;
            return (
              <div key={v.title} className="flex gap-4">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Ic size={20} className="text-[#1e3a8a]" weight="duotone" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-lg">{v.title}</div>
                  <div className="text-sm text-slate-600 leading-relaxed">{v.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Story stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
        {[
          { k: "2015", l: "Founded" },
          { k: "10K+", l: "Customers served" },
          { k: "4+",   l: "Branches" },
          { k: "100+", l: "Services offered" },
        ].map((s) => (
          <div key={s.l} className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
            <div className="text-3xl font-bold text-[#1e3a8a]" style={{ fontFamily: "var(--font-heading)" }}>{s.k}</div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-1">{s.l}</div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="text-center rounded-3xl bg-slate-900 text-white p-12 relative overflow-hidden">
        <div className="absolute -bottom-16 -right-16 w-60 h-60 bg-orange-500/20 rounded-full blur-3xl" />
        <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Ready to experience DigitalSetu?</h2>
        <p className="text-slate-300 mb-6 max-w-xl mx-auto">Apply for any service online, or visit us at your nearest branch.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/apply" className="inline-flex items-center gap-1.5 px-6 h-11 rounded-full bg-[#ea580c] hover:bg-orange-700 text-white text-sm font-semibold">Apply Online <ArrowRight size={13} /></Link>
          <Link to="/branches" className="inline-flex items-center gap-1.5 px-6 h-11 rounded-full bg-white/10 border border-white/25 text-white text-sm font-semibold hover:bg-white/20"><Buildings size={13} /> Find a Branch</Link>
        </div>
      </div>
    </div>
  );
}
